<template>
  <div class="container mx-auto py-6">
    <Toast />
    <Dialog v-model:visible="confirmDialogVisible" :style="{width: '500px'}" :modal="true" :closable="false" header="Konfirmasi Penerimaan Sanggah">
      <div class="confirmation-content">
        <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem; color: var(--yellow-500)" />
        <span>Apakah anda yakin akan menerima sanggah ini dengan opsi <strong>{{ selectedTipeLabel }}</strong>?</span>
        <div v-if="selectedTipeDiterima === 'evaluasi_ulang'" class="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-sm">
          <p class="text-amber-800"><strong>Perhatian:</strong> Data evaluasi sebelumnya akan diarsipkan dan di-reset. Proses evaluasi akan dimulai ulang.</p>
        </div>
        <div v-else-if="selectedTipeDiterima === 'pengadaan_ulang'" class="mt-3 p-3 bg-red-50 border border-red-200 rounded text-sm">
          <p class="text-red-800"><strong>Perhatian:</strong> Dokumen pengadaan akan dibatalkan. Pengadaan harus dilakukan dari awal.</p>
        </div>
        <p class="mt-4 font-semibold" v-if="countdownActive">Tombol konfirmasi akan aktif dalam {{ countdownSeconds }} detik</p>
      </div>
      <template #footer>
        <Button label="Batal" icon="pi pi-times" class="p-button-text" @click="confirmDialogVisible = false" />
        <Button label="Konfirmasi" icon="pi pi-check" class="p-button-danger" :disabled="countdownActive" @click="submitSanggah" autofocus />
      </template>
    </Dialog>
    <div class="bg-white rounded-lg shadow-lg p-6">
      <div class="mb-6">
        <h2 class="text-2xl font-semibold">Proses Sanggah</h2>
        <p class="text-gray-600">{{ dokumenPengadaan.paket_pengadaan?.nama_paket_pengadaan }}</p>
      </div>

      <div v-if="$page.props.flash.message" class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
        {{ $page.props.flash.message }}
      </div>

      <div class="p-4">
        <!-- Tipe Penerimaan Sanggah Selection -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-3">
            Pilih Tipe Penerimaan Sanggah <span class="text-red-500">*</span>
          </label>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Option 1: Pengadaan Ulang -->
            <div 
              @click="selectedTipeDiterima = 'pengadaan_ulang'"
              :class="[
                'border-2 rounded-lg p-4 cursor-pointer transition-all',
                selectedTipeDiterima === 'pengadaan_ulang' 
                  ? 'border-red-500 bg-red-50' 
                  : 'border-gray-200 hover:border-gray-300'
              ]"
            >
              <div class="flex items-start">
                <div class="flex-shrink-0">
                  <div :class="[
                    'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                    selectedTipeDiterima === 'pengadaan_ulang' 
                      ? 'border-red-500' 
                      : 'border-gray-300'
                  ]">
                    <div v-if="selectedTipeDiterima === 'pengadaan_ulang'" class="w-3 h-3 rounded-full bg-red-500"></div>
                  </div>
                </div>
                <div class="ml-3">
                  <h4 class="font-semibold text-gray-900">Terima dengan Pengadaan Ulang</h4>
                  <p class="text-sm text-gray-600 mt-1">
                    Dokumen pengadaan akan <strong>dibatalkan</strong>. Proses pengadaan harus dimulai dari awal dengan membuat dokumen baru.
                  </p>
                  <div class="mt-2 flex flex-wrap gap-1">
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                      Batalkan Dokumen
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Option 2: Evaluasi Ulang -->
            <div 
              @click="selectedTipeDiterima = 'evaluasi_ulang'"
              :class="[
                'border-2 rounded-lg p-4 cursor-pointer transition-all',
                selectedTipeDiterima === 'evaluasi_ulang' 
                  ? 'border-amber-500 bg-amber-50' 
                  : 'border-gray-200 hover:border-gray-300'
              ]"
            >
              <div class="flex items-start">
                <div class="flex-shrink-0">
                  <div :class="[
                    'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                    selectedTipeDiterima === 'evaluasi_ulang' 
                      ? 'border-amber-500' 
                      : 'border-gray-300'
                  ]">
                    <div v-if="selectedTipeDiterima === 'evaluasi_ulang'" class="w-3 h-3 rounded-full bg-amber-500"></div>
                  </div>
                </div>
                <div class="ml-3">
                  <h4 class="font-semibold text-gray-900">Terima dengan Evaluasi Ulang</h4>
                  <p class="text-sm text-gray-600 mt-1">
                    Data evaluasi akan <strong>diarsipkan</strong> dan di-<strong>reset</strong>. Peserta dan penawaran tetap ada, hanya evaluasi yang dilakukan ulang.
                  </p>
                  <div class="mt-2 flex flex-wrap gap-1">
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                      Arsipkan Data
                    </span>
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                      Reset Evaluasi
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Info Box based on selection -->
          <div v-if="selectedTipeDiterima" class="mt-4 p-3 rounded-lg text-sm" :class="tipeDiterimaInfoClass">
            <div class="flex">
              <i class="pi pi-info-circle mr-2 mt-0.5"></i>
              <div>
                <p v-if="selectedTipeDiterima === 'pengadaan_ulang'">
                  <strong>Yang akan terjadi:</strong> Status dokumen pengadaan akan berubah menjadi "ditolak". 
                  Untuk melanjutkan pengadaan, Anda harus membuat dokumen pengadaan baru.
                </p>
                <p v-else-if="selectedTipeDiterima === 'evaluasi_ulang'">
                  <strong>Yang akan terjadi:</strong>
                  <ul class="list-disc ml-4 mt-1">
                    <li>Semua data penilaian akan diarsipkan ke riwayat</li>
                    <li>Data penilaian penyedia akan dihapus</li>
                    <li>Status evaluasi peserta akan di-reset</li>
                    <li>BAEP dan BAHP akan di-reset</li>
                    <li>Data penawaran tetap tersimpan</li>
                  </ul>
                </p>
              </div>
            </div>
          </div>
        </div>

        <hr class="my-6">

        <div class="mb-4">
          <p>Apakah Anda yakin ingin menerima sanggah ini?</p>
          <div class="mt-2 text-yellow-600">
            <i class="pi pi-info-circle mr-2"></i>
            <span>Tindakan ini tidak dapat dibatalkan. Harap membaca syarat dan ketentuan penerimaan sanggah dan isi keterangan untuk melanjutkan.</span>
          </div>
        </div>
        
        <!-- EULA section -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Syarat dan Ketentuan Penerimaan Sanggah
          </label>
          <div 
            class="border rounded-lg p-4 bg-gray-50 h-48 overflow-y-auto text-sm"
            @scroll="handleEulaScroll($event)" 
          >
            <p class="mb-2">PERNYATAAN PERSETUJUAN PENERIMAAN SANGGAH
              Sistem Pengadaan Universitas Diponegoro</p>
            
            <section class="py-6">
              <ol class="list-decimal pl-5 space-y-2">
                <li>Saya telah membaca, memahami, dan menyetujui semua prosedur dan ketentuan terkait penerimaan sanggah dalam dokumen pengadaan.</li>
                <li>Saya mengakui bahwa penerimaan sanggah akan menyebabkan dokumen pengadaan ini dibatalkan dan status dokumen akan berubah menjadi "ditolak".</li>
                <li>Saya bertanggung jawab penuh atas keputusan penerimaan sanggah ini dan telah memastikan keputusan ini dibuat berdasarkan pertimbangan yang matang.</li>
                <li>Semua data dan informasi yang saya berikan dalam proses penerimaan sanggah ini adalah benar dan dapat dipertanggungjawabkan.</li>
                <li>Saya mengakui bahwa tindakan penerimaan sanggah ini tidak dapat dibatalkan setelah disetujui.</li>
                <li>Saya memahami bahwa penerimaan sanggah akan dicatat dalam sistem dan akan terlihat oleh semua pihak yang terlibat dalam proses pengadaan.</li>
              </ol>
            </section>
          </div>
        </div>

        <!-- Catatan field -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Catatan Tambahan
          </label>
          <Textarea v-model="catatan" rows="5" class="w-full" placeholder="Masukkan catatan tambahan untuk penerimaan sanggah..."></Textarea>
        </div>

        <!-- Signature pad -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Tanda Tangan Digital
          </label>
          <div class="border border-gray-300 rounded-lg p-2 bg-white">
            <VueSignaturePad ref="signaturePad" width="100%" height="200px" :options="{ backgroundColor: '#fff' }" @endStroke="onSignatureEnd"/>
          </div>
          <div class="flex mt-2 space-x-2">
            <Button label="Hapus Tanda Tangan" icon="pi pi-trash" class="p-button-outlined p-button-danger" @click="clearSignature" />
          </div>
        </div>

        <!-- Confirm checkbox -->
        <div class="mb-4">
          <div class="flex items-center">
            <Checkbox v-model="eulaConfirmed" :binary="true" inputId="eula-checkbox" />
            <label for="eula-checkbox" class="ml-2 text-gray-700">
              Saya telah membaca dan menyetujui syarat dan ketentuan di atas
            </label>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="flex justify-end space-x-4 mt-6">
          <Button label="Batal" icon="pi pi-times" class="p-button-outlined" @click="confirmBackNavigation" />
          <Button :label="submitButtonLabel" icon="pi pi-check" class="p-button-danger" 
                  :disabled="!isFormValid || cooldownActive" 
                  :loading="loading"
                  @click="confirmSanggah" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3'
import { Inertia } from '@inertiajs/inertia'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'
import Button from 'primevue/button'
import Textarea from 'primevue/textarea'
import Checkbox from 'primevue/checkbox'
import Dialog from 'primevue/dialog'
import { VueSignaturePad } from 'vue-signature-pad'

export default {
  components: {
    Toast,
    Button,
    Textarea,
    Checkbox,
    Dialog,
    VueSignaturePad
  },

  props: {
    dokumenPengadaan: Object,
    sanggah: Object
  },

  setup() {
    const toast = useToast();
    return { toast };
  },

  data() {
    return {
      loading: false,
      catatan: '',
      eulaRead: false,
      eulaConfirmed: false,
      signatureIsEmpty: true,
      countdownActive: false,
      countdownSeconds: 5,
      cooldownTimer: null,
      submitButtonLabel: 'Terima Sanggah',
      confirmDialogVisible: false,
      selectedTipeDiterima: null, // 'pengadaan_ulang' or 'evaluasi_ulang'
    }
  },

  computed: {
    isFormValid() {
      // Check if tipe diterima is selected, eula confirmed, and catatan is filled
      const isValid = this.selectedTipeDiterima &&
             this.eulaConfirmed && 
             this.catatan.trim().length > 0;
      
      console.log('Form validation status:', {
        selectedTipeDiterima: this.selectedTipeDiterima,
        eulaConfirmed: this.eulaConfirmed,
        signatureIsEmpty: this.signatureIsEmpty,
        catatanLength: this.catatan.trim().length,
        isValid: isValid
      });
      
      return isValid;
    },
    selectedTipeLabel() {
      const labels = {
        'pengadaan_ulang': 'Pengadaan Ulang',
        'evaluasi_ulang': 'Evaluasi Ulang',
        'penawaran_ulang': 'Pengajuan Penawaran Ulang'
      };
      return labels[this.selectedTipeDiterima] || '';
    },
    tipeDiterimaInfoClass() {
      if (this.selectedTipeDiterima === 'pengadaan_ulang') {
        return 'bg-red-50 border border-red-200 text-red-800';
      } else if (this.selectedTipeDiterima === 'evaluasi_ulang') {
        return 'bg-amber-50 border border-amber-200 text-amber-800';
      }
      return 'bg-gray-50 border border-gray-200 text-gray-800';
    }
  },

  mounted() {
    // Add event listener for beforeunload to prevent accidental navigation away
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },

  beforeUnmount() {
    // Remove event listener when component is destroyed
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    // Clear any existing timer
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
    }
  },

  methods: {
    onSignatureEnd() {
      // Update signature status - this triggers computed re-render
      if (this.$refs.signaturePad) {
        this.signatureIsEmpty = this.$refs.signaturePad.isEmpty();
        console.log('Signature updated, isEmpty:', this.signatureIsEmpty);
      } else {
        console.error('Signature pad reference not found');
      }
    },

    clearSignature() {
      this.$refs.signaturePad.clearSignature();
      this.signatureIsEmpty = true;
    },

    handleBeforeUnload(e) {
      // This creates the standard browser dialog that asks users if they want to leave the page
      e.preventDefault();
      e.returnValue = '';
    },

    confirmBackNavigation() {
      if (confirm('Anda yakin ingin kembali? Semua data yang telah diisi akan hilang.')) {
        // Navigate back
        window.history.back();
      }
    },

    // We've removed the startCooldownTimer method since we now use the countdown in the confirmation dialog

    handleEulaScroll(event) {
      const element = event.target;
      const reachedBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 1;
      
      if (reachedBottom) {
        this.eulaRead = true;
      }
    },

    confirmSanggah() {
      console.log('confirmSanggah method called');
      
      // Check if tipe diterima is selected
      if (!this.selectedTipeDiterima) {
        this.toast.add({
          severity: 'warn',
          summary: 'Peringatan',
          detail: 'Silakan pilih tipe penerimaan sanggah terlebih dahulu',
          life: 3000,
        });
        return;
      }
      
      if (!this.isFormValid) {
        console.log('Form validation failed, returning early');
        return;
      }
      
      // Check if signature is valid
      if (!this.$refs.signaturePad || this.$refs.signaturePad.isEmpty()) {
        this.toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Tanda tangan tidak boleh kosong',
          life: 3000,
        });
        return;
      }

      // Show confirmation dialog and start countdown
      this.confirmDialogVisible = true;
      this.countdownActive = true;
      this.countdownSeconds = 5;
      
      // Start the countdown timer
      this.cooldownTimer = setInterval(() => {
        this.countdownSeconds--;
        
        if (this.countdownSeconds <= 0) {
          clearInterval(this.cooldownTimer);
          this.countdownActive = false;
        }
      }, 1000);
    },
    
    async submitSanggah() {
      // This is called after the countdown when user clicks Confirm
      console.log('submitSanggah method called');
      this.confirmDialogVisible = false;
      this.loading = true;

      try {
        // Check if sanggah ID is available
        if (!this.sanggah || !this.sanggah.id) {
          console.error('Sanggah ID not found:', this.sanggah);
          throw new Error('Sanggah ID tidak ditemukan');
        }

        console.log('Checking signature...');
        if (!this.$refs.signaturePad) {
          console.error('Signature pad reference is null');
          throw new Error('Signature pad not initialized');
        }

        const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
        console.log('Signature data:', { isEmpty, dataLength: data ? data.length : 0 });
        
        if (isEmpty) {
          this.loading = false;
          this.toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Tanda tangan tidak boleh kosong',
            life: 3000,
          });
          return;
        }

        // Convert Base64 to Blob/File
        const res = await fetch(data);
        const blob = await res.blob();
        const file = new File([blob], `signature-sanggah-${this.dokumenPengadaan.id}-${Date.now()}.png`, {
          type: blob.type
        });

        // Create FormData
        const form = new FormData();
        form.append('signature', file);
        form.append('catatan', this.catatan);
        form.append('eula', this.eulaConfirmed ? 1 : 0);
        form.append('tipe_diterima', this.selectedTipeDiterima);
        
        // Get the URL for the endpoint
        const url = route('sanggah.accept', this.sanggah.id);
        console.log('Submitting to URL:', url);
        console.log('Tipe Diterima:', this.selectedTipeDiterima);
        
        // Post to backend using the sanggah.accept endpoint
        const response = await axios.post(url, form);
        console.log('Response:', response);
        
        // Check if the response is an actual JSON success response and not a debug dump
        if (!response.data || typeof response.data !== 'object' || !response.data.hasOwnProperty('success')) {
          console.error('Received unexpected response format (possibly a dump/dd output):', response);
          throw new Error('Received a debug response from the server. Check server logs.');
        }
        
        if (!response.data.success) {
          throw new Error(response.data.message || 'Terjadi kesalahan pada server');
        }
        
        this.toast.add({
          severity: 'success',
          summary: 'Berhasil',
          detail: response.data.message || 'Sanggah berhasil diterima dan dokumen pengadaan telah dibatalkan',
          life: 3000,
        });
        
        // Redirect back to sanggah list after showing success message
        setTimeout(() => {
          const redirectUrl = route('sanggah.index', this.dokumenPengadaan.id);
          console.log('Redirecting to:', redirectUrl);
          this.$inertia.visit(redirectUrl, {
            method: 'get',
            replace: true,
            preserveState: false,
            preserveScroll: false,
          });
        }, 1500);
        
      } catch (error) {
        console.error('Error:', error);
        console.log('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          url: error.config?.url,
          method: error.config?.method,
          stack: error.stack
        });
        
        // Specific error handling based on error type
        let errorMessage = 'Terjadi kesalahan saat memproses sanggah';
        
        if (error.message === 'Sanggah ID tidak ditemukan') {
          errorMessage = 'ID Sanggah tidak ditemukan. Silakan kembali ke halaman daftar sanggah.';
        } else if (error.message === 'Tanda tangan tidak boleh kosong') {
          errorMessage = 'Tanda tangan digital tidak boleh kosong.';
        } else if (error.response) {
          // Server responded with an error status
          errorMessage = error.response.data?.message || `Error ${error.response.status}: Terjadi kesalahan pada server`;
        } else if (error.request) {
          // Request was made but no response received
          errorMessage = 'Tidak ada respons dari server. Periksa koneksi internet Anda.';
        }
        
        this.toast.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
          life: 5000,
        });
      } finally {
        this.loading = false;
      }
    }
  },

  mounted() {
    // Ensure signature pad is properly sized
    this.$nextTick(() => {
      if (this.$refs.signaturePad) {
        this.$refs.signaturePad.resizeCanvas();
      }
    });
  }
}
</script>

<style scoped>
.vue-signature-pad {
  width: 100%;
  height: 200px;
}
</style>
